import { axiosPost } from "@root/utils/axiosForRails";
import { sendErrorLog } from "@root/utils/sendErrorLog";
import {
  type SecondaryContentPostProps,
  type SecondarySearchHitCassettes,
  type ShopWorkCassetteProps,
} from "@userFrontend/features/publication/types/Cassettes";
import { type AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import {
  chooseSalaryUnit,
  omitEmploymentName,
  sliceSalaryPrice,
} from "@userFrontend/features/publication/utils/formatSalary";

type ResponseProps = {
  data: {
    cassettes: SecondarySearchHitCassettes;
  };
};

type UseShopWorkCassettesArgs = {
  url: SecondaryContentPostProps["url"];
  params: SecondaryContentPostProps["params"];
  imgSrc: string;
  aggregateKey: string;
  workCode: string;
  query: string;
  shopWorks?: ShopWorkCassetteProps[];
  totalNumber: number;
};

export const useShopWorkCassettes = ({
  url,
  params,
  imgSrc,
  aggregateKey,
  workCode,
  query,
  shopWorks = [],
  totalNumber,
}: UseShopWorkCassettesArgs) => {
  const [shops, setShops] = useState(shopWorks);
  const [page, setPage] = useState(1);
  const [isShowIndicator, setIsShowIndicator] = useState(true);

  // 受け取ったデータを、使用するコンポーネントのpropsに合わせて整形する
  const formatData = useCallback(
    (fetchedShops: SecondarySearchHitCassettes): ShopWorkCassetteProps[] => {
      const formattedData = fetchedShops.map((shop) => {
        const employmentAndSalaryType = shop.salaries.map((salary) => ({
          employmentName: omitEmploymentName(salary.employmentTypeName),
          salaryMax: sliceSalaryPrice(salary.maxPrice),
          salaryMinUnit: chooseSalaryUnit(salary.maxPrice),
          salaryMin: sliceSalaryPrice(salary.minPrice),
          salaryMaxUnit: chooseSalaryUnit(salary.minPrice),
          salaryTypeName: salary.salaryTypeName,
        }));

        return {
          docId: shop.docId,
          imgSrc,
          url: shop.detailUrl,
          title: shop.title,
          shopName: shop.shopName,
          shopAddress: {
            prefecture: shop.shopAddress.prefecture,
            city: shop.shopAddress.city,
            station: shop.shopAddress.station,
          },
          employmentAndSalaryType,
          enterableFlag: shop.actionContent.entry.enterable,
          entriedFlag: shop.actionContent.entry.hasEntered,
          scoutedFlag: shop.actionContent.scoutApprove.scoutedFlag,
          workCode,
          scoutedEntryUrl: shop.actionContent.scoutApprove.scoutedEntryUrl,
          query,
          entryUrl: shop.actionContent.entry.url,
        };
      });
      return formattedData;
    },
    [imgSrc, query, workCode],
  );

  const fetchShopWorksData = useCallback(async () => {
    await axiosPost
      .post(url, { ...params, page, aggregate_key: aggregateKey })
      .then((res: ResponseProps) => {
        // 重複する店舗の求人を除外してnewShopsに格納する
        const fetchedShops = formatData(res.data.cassettes);
        const shopWorksDocIds = shops.map((shopWork) => shopWork.docId);
        const newShops = fetchedShops.filter((shop) => !shopWorksDocIds.includes(shop.docId));

        // 万が一取得できる店舗の求人がなくなってもインジケーターが残っていた場合は、インジケーターを非表示にして処理を抜ける
        if (!newShops.length) {
          setIsShowIndicator(false);
          return;
        }

        // 既存の店舗の求人に追加分の店舗の求人を加える
        setShops((prev) => [...prev, ...newShops]);
        setPage((prev) => prev + 1);
      })
      .catch((error: AxiosError) => {
        sendErrorLog(new Error(error.message || "求人カセットのデータを取得できませんでした"));
      });
  }, [url, params, page, aggregateKey, formatData, shops]);

  const handleInView = useCallback(() => {
    fetchShopWorksData();
  }, [fetchShopWorksData]);

  // 全ての店舗ごと求人を表示したらインジケーターを非表示にする
  useEffect(() => {
    if (shops.length >= totalNumber) {
      setIsShowIndicator(false);
    }
  }, [shops.length, totalNumber]);

  return { shops, handleInView, isShowIndicator };
};
